// LoginPage.jsx — M1. Two tabs: login-code and password, against the SB-006
// cookie-auth backend (dc_session / dc_device set HttpOnly server-side; this page
// never touches cookies). Endpoint map:
//   send code   → POST /api/auth/request-code   { email }
//   code login  → POST /api/auth/verify-code     { email, code, tz }
//   pwd login   → POST /api/auth/login-password  { email, password }
// On success: refresh() the session, then navigate to "/".
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import api, { ApiError } from '../api/client.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import { useLang, useT } from '../i18n/index.mjs';

// Pragmatic email shape check; the backend is the authority.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export default function LoginPage() {
  const t = useT();
  const { toggleLang } = useLang();
  const { account, loading, refresh } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('code'); // 'code' | 'password'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState(''); // inline validation / login error
  const [notice, setNotice] = useState(''); // neutral info (code-sent, tz hint)
  const { snack, notify, clearSnack } = useNotify(); // transient errors (network)
  const [cooldown, setCooldown] = useState(0); // resend cooldown seconds
  const timerRef = useRef(null);
  const tz = browserTz();

  // Already signed in (e.g. visited /login with a live session) → leave.
  useEffect(() => {
    if (!loading && account) navigate('/', { replace: true });
  }, [loading, account, navigate]);

  // Resend cooldown countdown.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setInterval(() => {
      setCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const clearMessages = () => {
    setFieldError('');
  };

  const validEmail = () => {
    if (!email.trim()) {
      setFieldError(t('login.error.emailRequired'));
      return false;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setFieldError(t('login.error.emailInvalid'));
      return false;
    }
    return true;
  };

  // ── validation-code: send ────────────────────────────────────────────────
  const onSendCode = async () => {
    clearMessages();
    if (!validEmail() || cooldown > 0 || busy) return;
    setBusy(true);
    try {
      await api.requestCode(email.trim());
      // Anti-enumeration: always present the neutral "if valid, sent" message.
      setNotice(t('login.hint.codeSent'));
      setCooldown(60);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        // Cooldown / daily cap — honour the backend's retryAfterSeconds.
        const wait = Number.isFinite(e.retryAfterSeconds) ? Math.max(1, e.retryAfterSeconds) : 60;
        setNotice(t('login.hint.codeSent'));
        setCooldown(wait);
      } else if (e instanceof ApiError && e.status === 0) {
        notify(t('login.error.network'), 'error');
      } else {
        // Any other status (incl. 400) still shows the neutral message so the
        // UI never reveals whether the address exists.
        setNotice(t('login.hint.codeSent'));
        setCooldown(60);
      }
    } finally {
      setBusy(false);
    }
  };

  // ── validation-code: verify / login ──────────────────────────────────────
  const onCodeLogin = async () => {
    clearMessages();
    if (!validEmail() || busy) return;
    if (!code.trim()) {
      setFieldError(t('login.error.codeRequired'));
      return;
    }
    setBusy(true);
    try {
      await api.verifyCode(email.trim(), code.trim(), tz);
      await refresh();
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setFieldError(t('login.error.invalidCode'));
      } else if (e instanceof ApiError && e.status === 0) {
        notify(t('login.error.network'), 'error');
      } else {
        setFieldError(t('login.error.generic'));
      }
    } finally {
      setBusy(false);
    }
  };

  // ── password login ───────────────────────────────────────────────────────
  const onPasswordLogin = async () => {
    clearMessages();
    if (!validEmail() || busy) return;
    if (!password) {
      setFieldError(t('login.error.passwordRequired'));
      return;
    }
    setBusy(true);
    try {
      await api.loginPassword(email.trim(), password);
      await refresh();
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // PASSWORD_LOGIN_REFUSED — uniform message, never the specific cause;
        // steer the user to the code tab (B3 / auth-flow §4).
        setFieldError(t('login.error.passwordRefused'));
        setTab('code');
      } else if (e instanceof ApiError && e.status === 0) {
        notify(t('login.error.network'), 'error');
      } else {
        setFieldError(t('login.error.generic'));
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (tab === 'code') onCodeLogin();
    else onPasswordLogin();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 440 }} elevation={3}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 700 }} variant="h5" component="h1">
              {t('login.title')}
            </Typography>
            <Button size="small" color="inherit" onClick={toggleLang} aria-label={t('app.langToggle.aria')}>
              {t('app.langToggle')}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('login.subtitle')}
          </Typography>

          <Tabs
            value={tab}
            onChange={(_e, v) => {
              setTab(v);
              clearMessages();
            }}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab value="code" label={t('login.tab.code')} />
            <Tab value="password" label={t('login.tab.password')} />
          </Tabs>

          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                type="email"
                label={t('login.field.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                // username（不是 email）：密码管理器按这一标注把已存凭据配给账号，
                // 与设密码对话框里那个隐藏的 username 是同一把钥匙（第三百八十六批）。
                autoComplete="username"
                fullWidth
                required
                disabled={busy}
              />

              {tab === 'code' ? (
                <>
                  <Stack sx={{ alignItems: 'flex-start' }} direction="row" spacing={1}>
                    <TextField
                      label={t('login.field.code')}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      autoComplete="one-time-code"
                      slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                      fullWidth
                      disabled={busy}
                    />
                    <Button
                      variant="outlined"
                      onClick={onSendCode}
                      disabled={busy || cooldown > 0}
                      sx={{ whiteSpace: 'nowrap', mt: 0.5, minWidth: 128 }}
                    >
                      {cooldown > 0
                        ? t('login.action.resendIn', { s: cooldown })
                        : t('login.action.sendCode')}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t('login.hint.tz', { tz })}
                  </Typography>
                  {/* 第七十批：限额的静默是防枚举设计（OQ-B20 统一 200），但对真实成员
                      它读起来像"坏了"——mayank 烧完 3 枚/日后对着"已发送"等不来邮件，
                      成了第一个真实用户的第一次求助。提示写成**无条件静态文本**：
                      不看请求结果、不看账号状态，故一个比特都不泄露。 */}
                  <Typography variant="caption" color="text.secondary">
                    {t('login.hint.codeCap')}
                  </Typography>
                </>
              ) : (
                <>
                  <TextField
                    type="password"
                    label={t('login.field.password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    fullWidth
                    disabled={busy}
                  />
                  {/* 第三百八十六批：拒绝回执是统一的（不说哪一条没过，防枚举），于是
                      「七天窗」与「浏览器填了旧密码」这两个最常见的原因只能靠一句
                      **无条件静态文本**说清——与 codeCap 那句同一路子，一个比特都不泄露。 */}
                  <Typography variant="caption" color="text.secondary">
                    {t('login.hint.passwordWindow')}
                  </Typography>
                </>
              )}

              {notice ? <Alert severity="info">{notice}</Alert> : null}
              {fieldError ? <Alert severity="error">{fieldError}</Alert> : null}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {t('login.action.login')}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <AppSnackbar snack={snack} onClose={clearSnack} autoHideDuration={5000} />
    </Box>
  );
}
